import React, { useState } from 'react';
import { 
  Bell, 
  Mail, 
  Settings, 
  Check, 
  X, 
  Clock, 
  AlertTriangle, 
  Info, 
  CheckCircle,
  FileText,
  Shield,
  Trash2,
  Search
} from 'lucide-react';
import { formatDateTime } from '../utils/dateFormat';

interface Notification {
  id: string;
  type: 'info' | 'warning' | 'success' | 'error';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  category: 'case' | 'system' | 'security' | 'update';
  actionUrl?: string;
}

export const NotificationsPage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState<'inbox' | 'preferences' | 'history'>('inbox');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterRead, setFilterRead] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Mock notifications
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: '1',
      type: 'info',
      title: 'New Case Assignment',
      message: 'You have been assigned to Case #MC-2024-003 - Emergency Medicine Review',
      timestamp: '2024-01-15T14:30:00Z',
      read: false,
      category: 'case',
      actionUrl: '/cases/MC-2024-003'
    },
    {
      id: '2',
      type: 'warning',
      title: 'Document Upload Required',
      message: 'Case #MC-2024-001 is missing required medical reports. Please upload within 24 hours.',
      timestamp: '2024-01-15T12:15:00Z',
      read: false,
      category: 'case',
      actionUrl: '/cases/MC-2024-001'
    },
    {
      id: '3',
      type: 'success',
      title: 'Case Processing Complete',
      message: 'Case #MC-2024-002 has been successfully processed and is ready for review.',
      timestamp: '2024-01-15T10:45:00Z',
      read: true,
      category: 'case',
      actionUrl: '/cases/MC-2024-002'
    },
    {
      id: '4',
      type: 'error',
      title: 'Security Alert',
      message: 'Multiple failed login attempts detected from IP 203.0.113.45',
      timestamp: '2024-01-15T09:20:00Z',
      read: false,
      category: 'security'
    },
    {
      id: '5',
      type: 'info',
      title: 'System Maintenance',
      message: 'Scheduled maintenance will occur on January 20th from 2:00 AM to 4:00 AM GMT',
      timestamp: '2024-01-14T16:00:00Z',
      read: true,
      category: 'system'
    }
  ]);

  const [preferences, setPreferences] = useState({
    email: {
      caseUpdates: true,
      systemAlerts: true,
      securityAlerts: true,
      weeklyDigest: false
    },
    desktop: {
      caseUpdates: true,
      systemAlerts: false,
      securityAlerts: true,
      sound: false
    },
    frequency: {
      immediate: true,
      hourly: false,
      daily: false
    }
  });

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'error':
        return <X className="h-5 w-5 text-red-500" />;
      default:
        return <Info className="h-5 w-5 text-cyan-500" />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'case':
        return <FileText className="h-4 w-4" />;
      case 'security':
        return <Shield className="h-4 w-4" />;
      case 'system':
        return <Settings className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const markAsRead = (id: string) => {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const markAsUnread = (id: string) => {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, read: false } : n
    ));
  };

  const deleteNotification = (id: string) => {
    setNotifications(notifications.filter(n => n.id !== id));
  };

  const filteredNotifications = notifications.filter(notification => {
    const matchesCategory = filterCategory === 'all' || notification.category === filterCategory;
    const matchesRead = filterRead === 'all' || 
      (filterRead === 'unread' && !notification.read) ||
      (filterRead === 'read' && notification.read);
    const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         notification.message.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesRead && matchesSearch;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Bell className="h-8 w-8 text-primary-600" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-medical-900">Notifications</h1>
            <p className="text-medical-600">
              {unreadCount > 0 ? `${unreadCount} unread notifications` : 'All notifications read'}
            </p>
          </div>
        </div>
        <button className="btn-primary">Mark All as Read</button>
      </div>

      {/* Tabs */}
      <div className="border-b border-medical-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'inbox', label: 'Inbox', icon: Bell, count: unreadCount },
            { key: 'preferences', label: 'Preferences', icon: Settings },
            { key: 'history', label: 'History', icon: Clock }
          ].map(({ key, label, icon: Icon, count }) => (
            <button
              key={key}
              onClick={() => setSelectedTab(key as any)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                selectedTab === key
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-medical-500 hover:text-medical-700 hover:border-medical-300'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>{label}</span>
              {count && count > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {selectedTab === 'inbox' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="card">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-64">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-medical-400" />
                  <input
                    type="text"
                    placeholder="Search notifications..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input-field pl-10"
                  />
                </div>
              </div>
              <div>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="input-field"
                >
                  <option value="all">All Categories</option>
                  <option value="case">Cases</option>
                  <option value="system">System</option>
                  <option value="security">Security</option>
                  <option value="update">Updates</option>
                </select>
              </div>
              <div>
                <select
                  value={filterRead}
                  onChange={(e) => setFilterRead(e.target.value)}
                  className="input-field"
                >
                  <option value="all">All</option>
                  <option value="unread">Unread</option>
                  <option value="read">Read</option>
                </select>
              </div>
            </div>
          </div>

          {/* Notifications List */}
          <div className="space-y-2">
            {filteredNotifications.length === 0 ? (
              <div className="card text-center py-8">
                <Bell className="h-12 w-12 text-medical-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-medical-900 mb-2">No notifications found</h3>
                <p className="text-medical-600">Try adjusting your filters or search terms</p>
              </div>
            ) : (
              filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`card hover:bg-medical-50 transition-colors ${
                    !notification.read ? 'border-l-4 border-l-primary-500 bg-primary-50/30' : ''
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 pt-1">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            {getCategoryIcon(notification.category)}
                            <h3 className={`text-sm font-medium ${
                              !notification.read ? 'text-medical-900' : 'text-medical-700'
                            }`}>
                              {notification.title}
                            </h3>
                            {!notification.read && (
                              <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
                            )}
                          </div>
                          <p className="text-sm text-medical-600 mb-2">
                            {notification.message}
                          </p>
                          <div className="flex items-center space-x-4 text-xs text-medical-500">
                            <span>{formatDateTime(notification.timestamp)}</span>
                            <span className="capitalize">{notification.category}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          {notification.actionUrl && (
                            <button className="btn-secondary text-xs">View</button>
                          )}
                          {!notification.read ? (
                            <button
                              onClick={() => markAsRead(notification.id)}
                              className="p-1 text-medical-400 hover:text-primary-600"
                              title="Mark as read"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => markAsUnread(notification.id)}
                              className="p-1 text-medical-400 hover:text-primary-600"
                              title="Mark as unread"
                            >
                              <Mail className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => deleteNotification(notification.id)}
                            className="p-1 text-medical-400 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {selectedTab === 'preferences' && (
        <div className="space-y-6">
          {/* Email Notifications */}
          <div className="card">
            <h3 className="text-lg font-medium text-medical-900 mb-4 flex items-center space-x-2">
              <Mail className="h-5 w-5" />
              <span>Email Notifications</span>
            </h3>
            <div className="space-y-4">
              {[
                { key: 'caseUpdates', label: 'Case Updates', description: 'Notifications about case status changes' },
                { key: 'systemAlerts', label: 'System Alerts', description: 'Important system notifications and maintenance' },
                { key: 'securityAlerts', label: 'Security Alerts', description: 'Security-related notifications and warnings' },
                { key: 'weeklyDigest', label: 'Weekly Digest', description: 'Weekly summary of activity and updates' }
              ].map(({ key, label, description }) => (
                <div key={key} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-medical-700">{label}</p>
                    <p className="text-xs text-medical-500">{description}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.email[key as keyof typeof preferences.email]}
                      onChange={(e) => setPreferences({
                        ...preferences,
                        email: {
                          ...preferences.email,
                          [key]: e.target.checked
                        }
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-medical-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-medical-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop Notifications */}
          <div className="card">
            <h3 className="text-lg font-medium text-medical-900 mb-4 flex items-center space-x-2">
              <Bell className="h-5 w-5" />
              <span>Desktop Notifications</span>
            </h3>
            <div className="space-y-4">
              {[
                { key: 'caseUpdates', label: 'Case Updates', description: 'Show desktop notifications for case changes' },
                { key: 'systemAlerts', label: 'System Alerts', description: 'Show desktop notifications for system events' },
                { key: 'securityAlerts', label: 'Security Alerts', description: 'Show desktop notifications for security events' },
                { key: 'sound', label: 'Sound Alerts', description: 'Play sound with desktop notifications' }
              ].map(({ key, label, description }) => (
                <div key={key} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-medical-700">{label}</p>
                    <p className="text-xs text-medical-500">{description}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.desktop[key as keyof typeof preferences.desktop]}
                      onChange={(e) => setPreferences({
                        ...preferences,
                        desktop: {
                          ...preferences.desktop,
                          [key]: e.target.checked
                        }
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-medical-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-medical-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Notification Frequency */}
          <div className="card">
            <h3 className="text-lg font-medium text-medical-900 mb-4 flex items-center space-x-2">
              <Clock className="h-5 w-5" />
              <span>Notification Frequency</span>
            </h3>
            <div className="space-y-3">
              {[
                { key: 'immediate', label: 'Immediate', description: 'Receive notifications as they happen' },
                { key: 'hourly', label: 'Hourly Digest', description: 'Receive notifications every hour' },
                { key: 'daily', label: 'Daily Digest', description: 'Receive notifications once per day' }
              ].map(({ key, label, description }) => (
                <div key={key} className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id={key}
                    name="frequency"
                    checked={preferences.frequency[key as keyof typeof preferences.frequency]}
                    onChange={() => setPreferences({
                      ...preferences,
                      frequency: {
                        immediate: key === 'immediate',
                        hourly: key === 'hourly',
                        daily: key === 'daily'
                      }
                    })}
                    className="w-4 h-4 text-primary-600 bg-medical-100 border-medical-300 focus:ring-primary-500"
                  />
                  <label htmlFor={key} className="flex-1">
                    <p className="text-sm font-medium text-medical-700">{label}</p>
                    <p className="text-xs text-medical-500">{description}</p>
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <button className="btn-primary">Save Preferences</button>
          </div>
        </div>
      )}

      {selectedTab === 'history' && (
        <div className="space-y-6">
          {/* Notification Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { label: 'Total Notifications', value: '247', icon: Bell, color: 'blue' },
              { label: 'This Week', value: '18', icon: Clock, color: 'green' },
              { label: 'Unread', value: unreadCount.toString(), icon: AlertTriangle, color: 'yellow' },
              { label: 'Response Rate', value: '94%', icon: CheckCircle, color: 'emerald' }
            ].map((stat, index) => (
              <div key={index} className="card text-center">
                <div className={`w-12 h-12 bg-${stat.color}-100 rounded-full flex items-center justify-center mx-auto mb-3`}>
                  <stat.icon className={`h-6 w-6 text-${stat.color}-600`} />
                </div>
                <h3 className="text-lg font-medium text-medical-900">{stat.label}</h3>
                <p className={`text-2xl font-bold text-${stat.color}-600`}>{stat.value}</p>
              </div>
            ))}
          </div>

          {/* Recent Activity */}
          <div className="card">
            <h3 className="text-lg font-medium text-medical-900 mb-4">Recent Activity</h3>
            <div className="space-y-3">
              {notifications.slice(0, 10).map((notification) => (
                <div key={notification.id} className="flex items-center justify-between p-3 bg-medical-50 rounded-md">
                  <div className="flex items-center space-x-3">
                    {getNotificationIcon(notification.type)}
                    <div>
                      <p className="text-sm font-medium text-medical-900">{notification.title}</p>
                      <p className="text-xs text-medical-500">
                        {formatDateTime(notification.timestamp)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      notification.read 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {notification.read ? 'Read' : 'Unread'}
                    </span>
                    <span className="text-xs text-medical-500 capitalize">{notification.category}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};